import { useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
    getDashboardV3OverviewFunnel,
    getDashboardV3OverviewKpi,
    getDashboardV3OverviewProductKpi,
    getDashboardV3OverviewProductTrend,
    getDashboardV3OverviewRetentionMatrix,
} from '../services/dashboardV3.service';
import type {
    DashboardV3OverviewFunnelData,
    DashboardV3OverviewKpiData,
    DashboardV3OverviewKpiParams,
    DashboardV3OverviewProductKpiData,
    DashboardV3OverviewProductType,
    DashboardV3OverviewRetentionMatrix,
    DashboardV3OverviewTrendData,
} from '../services/dashboardV3.types';

export type DashboardV3OverviewTabValue =
    | 'overview'
    | 'perps'
    | 'predictSpot'
    | 'predictLeveraged'
    | 'predictGrid';

export const OVERVIEW_PRODUCT_TYPE_MAP: Record<
    Exclude<DashboardV3OverviewTabValue, 'overview'>,
    DashboardV3OverviewProductType
> = {
    perps: 'perps',
    predictSpot: 'prediction_spot',
    predictLeveraged: 'prediction_leverage',
    predictGrid: 'prediction_grid',
};

const PRODUCT_TYPES: DashboardV3OverviewProductType[] = [
    'perps',
    'prediction_spot',
    'prediction_leverage',
    'prediction_grid',
];

const EMPTY_OVERVIEW_KPI: DashboardV3OverviewKpiData = {
    newUsers: 0,
    newUsersDelta: null,
    depositUsers: 0,
    depositUsersDelta: null,
    tradingUsers: 0,
    tradingUsersDelta: null,
    depositToTradeConversions: [],
    d1RetentionByProduct: [],
    platformTvl: null,
    platformTvlDelta: null,
    depositAmount: 0,
    depositAmountDelta: null,
    withdrawAmount: 0,
    withdrawAmountDelta: null,
};

const EMPTY_FUNNEL: DashboardV3OverviewFunnelData = {
    newUsers: 0,
    funnels: [],
};

const EMPTY_PRODUCT_KPI: DashboardV3OverviewProductKpiData = {
    productType: 'perps',
    tradingUsers: 0,
    tradingUsersDelta: null,
    tradingVolume: 0,
    tradingVolumeDelta: null,
    depositAmount: 0,
    depositAmountDelta: null,
    firstDepositUsers: null,
    firstDepositUsersDelta: null,
};

export function useDashboardV3OverviewData(
    params: DashboardV3OverviewKpiParams,
    activeTab: DashboardV3OverviewTabValue
) {
    const notify = useNotify();
    const t = useTranslate();
    const [overviewKpi, setOverviewKpi] = useState<DashboardV3OverviewKpiData>(EMPTY_OVERVIEW_KPI);
    const [funnel, setFunnel] = useState<DashboardV3OverviewFunnelData>(EMPTY_FUNNEL);
    const [productKpi, setProductKpi] = useState<DashboardV3OverviewProductKpiData>(EMPTY_PRODUCT_KPI);
    const [trends, setTrends] = useState<DashboardV3OverviewTrendData[]>([]);
    const [retentionMatrices, setRetentionMatrices] = useState<DashboardV3OverviewRetentionMatrix[]>([]);

    const [kpiLoading, setKpiLoading] = useState(false);
    const [funnelLoading, setFunnelLoading] = useState(false);
    const [retentionLoading, setRetentionLoading] = useState(false);
    const [trendsLoading, setTrendsLoading] = useState(false);

    const isOverviewTab = activeTab === 'overview';
    const productType = isOverviewTab ? undefined : OVERVIEW_PRODUCT_TYPE_MAP[activeTab];

    const trendProductTypes = useMemo(
        () => (productType ? [productType] : PRODUCT_TYPES),
        [productType]
    );

    const loadFailedMessage = t('module.dashboardV3.overview.messages.loadFailed', {
        _: 'Failed to load overview data',
    });

    /** KPI 卡片：总览 8 张卡 / 产品子 tab 4 张卡，受右上角时间筛选影响。 */
    useEffect(() => {
        let alive = true;
        setKpiLoading(true);

        const request = isOverviewTab
            ? getDashboardV3OverviewKpi(params).then((resp) => {
                if (alive) setOverviewKpi(resp?.data ?? EMPTY_OVERVIEW_KPI);
            })
            : getDashboardV3OverviewProductKpi({ ...params, productType: productType as DashboardV3OverviewProductType }).then((resp) => {
                if (alive) setProductKpi(resp?.data ?? { ...EMPTY_PRODUCT_KPI, productType: productType as DashboardV3OverviewProductType });
            });

        request
            .catch((error: any) => {
                if (!alive) return;
                notify(error?.message || loadFailedMessage, { type: 'warning' });
            })
            .finally(() => {
                if (alive) setKpiLoading(false);
            });

        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOverviewTab, params, productType, notify]);

    /** 转化漏斗：只在总览 tab 用，受右上角时间筛选影响。（当前前端展示已隐藏，接口继续拉，方案定了随时能接回去。） */
    useEffect(() => {
        if (!isOverviewTab) {
            setFunnel(EMPTY_FUNNEL);
            return;
        }

        let alive = true;
        setFunnelLoading(true);

        getDashboardV3OverviewFunnel(params)
            .then((resp) => {
                if (alive) setFunnel(resp?.data ?? EMPTY_FUNNEL);
            })
            .catch((error: any) => {
                if (!alive) return;
                notify(error?.message || loadFailedMessage, { type: 'warning' });
            })
            .finally(() => {
                if (alive) setFunnelLoading(false);
            });

        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOverviewTab, params, notify]);

    /**
     * 留存矩阵：只在总览 tab 用，队列固定自然日/周/月，不受右上角时间筛选影响——
     * 依赖数组里没有 params，切时间范围不会重新拉这块。
     */
    useEffect(() => {
        if (!isOverviewTab) {
            setRetentionMatrices([]);
            return;
        }

        let alive = true;
        setRetentionLoading(true);

        getDashboardV3OverviewRetentionMatrix()
            .then((resp) => {
                if (alive) setRetentionMatrices(resp?.data?.matrices ?? []);
            })
            .catch((error: any) => {
                if (!alive) return;
                notify(error?.message || loadFailedMessage, { type: 'warning' });
            })
            .finally(() => {
                if (alive) setRetentionLoading(false);
            });

        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOverviewTab, notify]);

    /**
     * 交易趋势图：固定最近 14 天，同样不受右上角时间筛选影响——依赖数组里没有 params。
     * 总览 tab 一次拉 4 个产品，产品子 tab 只拉当前产品这一个。
     */
    useEffect(() => {
        let alive = true;
        setTrendsLoading(true);

        Promise.all(trendProductTypes.map((item) => getDashboardV3OverviewProductTrend({ productType: item })))
            .then((responses) => {
                if (!alive) return;
                setTrends(responses.map((resp) => resp.data).filter(Boolean));
            })
            .catch((error: any) => {
                if (!alive) return;
                notify(error?.message || loadFailedMessage, { type: 'warning' });
            })
            .finally(() => {
                if (alive) setTrendsLoading(false);
            });

        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trendProductTypes, notify]);

    return {
        overviewKpi,
        funnel,
        productKpi,
        trends,
        retentionMatrices,
        kpiLoading,
        funnelLoading,
        retentionLoading,
        trendsLoading,
        // 兼容旧用法：任意一块还在加载就是 true。新代码请用上面那几个更细的 loading 分别控制各自区块。
        loading: kpiLoading || retentionLoading || trendsLoading,
    };
}
